import { useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { updateMember } from 'src/shared/services/memberService';
import { Member } from '../models/member';

interface FormValues {
  expireDate: string;
}

type MemberChangeExpireDateModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  refetchMemberCallback: () => void;
  member: Member;
};

export const MemberChangeExpireDateModal = (props: MemberChangeExpireDateModalProps) => {
  const { open, setOpen, refetchMemberCallback, member } = props;
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);

  const schema = yup.object().shape({
    expireDate: yup.string().required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      expireDate: member.expireDate,
    },
  });
  const { handleSubmit, reset } = formMethods;

  const onSubmit = async (data: FormValues) => {
    setLoading(true);

    const dataToPost: Member = { ...member, expireDate: DateHelper.getFSDate(data.expireDate) };
    try {
      await updateMember(dataToPost);
      toast.success(<>{t('member:memberDetails.successUpdatingExpireDate')}</>);
      setOpen(false);
      refetchMemberCallback();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('member:memberDetails.errorUpdatingExpireDate')}</>);
    } finally {
      setLoading(false);
    }
  };

  const onHandleClose = () => {
    reset();
    setOpen(false);
  };

  return (
    <FormProvider {...formMethods}>
      <form>
        <GenericDialog
          dialogTitle={t('member:memberDetails.changeExpireDate')}
          open={open}
          maxWidth='sm'
          showDialogActions
          dialogContent={
            <Grid container>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12}>
                <ControlledDatePicker name='expireDate' label={t('member:enterMember.expireDate')} />
              </Grid>
            </Grid>
          }
          onClose={onHandleClose}
          onSave={handleSubmit(onSubmit)}
        />
      </form>
    </FormProvider>
  );
};
